import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import {
  batchStatusBadgeClass,
  formatStatusLabel,
  STUDENT_STATUS_OPTIONS,
  deliveryStatusBadgeClass,
  deliveryStatusLabel,
} from '../../utils/batchStudentStatusHelpers';
import {
  formatDate,
  formatScheduleType,
  monthsSince,
  daysSince,
  attendanceBadgeClass,
} from '../../utils/productSubmissionHelpers';
import { attemptBadgeClass, attemptLabelClass, attemptLabel, formatMarks } from '../../utils/diplomaCompletionHelpers';

function LoadingSpinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white p-16 text-center">
      <svg className="h-9 w-9 animate-spin text-emerald-600" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <p className="text-sm font-medium text-gray-500">{label}</p>
    </div>
  );
}

function StudentAvatar({ imagePath, name }) {
  const [errored, setErrored] = useState(false);
  if (!imagePath || errored) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-400 ring-1 ring-gray-200"
        title={name || 'Student'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.5c-3.3 0-9.9 1.6-9.9 4.9v2.4h19.8v-2.4c0-3.3-6.6-4.9-9.9-4.9z" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={getImageURL(imagePath)}
      alt={name || 'Student'}
      onError={() => setErrored(true)}
      className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-gray-200"
    />
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#059669' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(5,150,105,0.2)' : 'none',
    '&:hover': { borderColor: '#059669' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#d1fae5' : state.isFocused ? '#ecfdf5' : 'white',
    color: state.isSelected ? '#065f46' : '#1f2937', cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
  multiValueLabel: (base) => ({ ...base, color: '#065f46' }),
};

const BATCH_STATUS_TABS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'not started', label: 'Not Started' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed', label: 'Batch Completed' },
  { value: 'all', label: 'All' },
];

function BatchPhaseSheetTable({ batch, onStatusChange }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkValue, setBulkValue] = useState('ongoing');
  const [bulkSaving, setBulkSaving] = useState(false);

  const allSelected = batch.students.length > 0 && selectedIds.size === batch.students.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(batch.students.map((s) => s.registrationId)));
  };

  const toggleOne = (registrationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(registrationId)) next.delete(registrationId);
      else next.add(registrationId);
      return next;
    });
  };

  const applyBulkStatus = async () => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    const ids = Array.from(selectedIds);
    let success = 0;
    let failure = 0;
    for (const registrationId of ids) {
      try {
        await axiosInstance.put(`/admin/registrations/${registrationId}`, {
          deliveryStatus: bulkValue === 'ongoing' ? '' : bulkValue,
        });
        onStatusChange(batch.batchId, registrationId, bulkValue === 'ongoing' ? null : bulkValue);
        success += 1;
      } catch (err) {
        failure += 1;
      }
    }
    setBulkSaving(false);
    setSelectedIds(new Set());
    Swal.fire({
      icon: failure ? 'warning' : 'success',
      title: 'Bulk update finished',
      text: `${success} student${success === 1 ? '' : 's'} updated${failure ? `, ${failure} failed` : ''}.`,
      confirmButtonColor: '#059669',
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-800">
              {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
            </p>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${batchStatusBadgeClass(batch.status)}`}>
              {formatStatusLabel(batch.status)}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
          {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
        </span>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-100 bg-amber-50 px-5 py-2.5">
          <span className="text-xs font-semibold text-amber-800">{selectedIds.size} selected</span>
          <select
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs focus:border-amber-500 focus:outline-none"
          >
            {STUDENT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyBulkStatus}
            disabled={bulkSaving}
            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {bulkSaving ? 'Applying…' : `Apply to ${selectedIds.size}`}
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left" style={{ minWidth: `${880 + batch.phases.length * 90}px` }}>
          <thead>
            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              <th className="border border-gray-200 px-3 py-2.5">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5" />
              </th>
              <th className="border border-gray-200 px-4 py-2.5">University ID</th>
              <th className="border border-gray-200 px-4 py-2.5">Student ID</th>
              <th className="border border-gray-200 px-4 py-2.5">Registration No.</th>
              <th className="border border-gray-200 px-4 py-2.5">Name</th>
              {batch.phases.map((phase) => (
                <th key={phase.phaseId} className="border border-gray-200 px-2 py-2.5 text-center" title={phase.phaseName}>
                  {phase.phaseName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {batch.students.map((s) => {
              const monthsPassed = s.universityRegisterDate ? monthsSince(s.universityRegisterDate) : null;
              return (
                <tr key={`${s.studentId}-${s.registrationNumber}`} className="hover:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.registrationId)}
                      onChange={() => toggleOne(s.registrationId)}
                      className="h-3.5 w-3.5"
                    />
                  </td>
                  <td className="border border-gray-200 px-4 py-2.5 text-xs">
                    <div className="font-medium text-gray-700">{s.universityID || '—'}</div>
                    {s.universityRegisterDate && (
                      <>
                        <div className="mt-0.5 text-gray-400">{formatDate(s.universityRegisterDate)}</div>
                        <div className="text-gray-400">{monthsPassed} mo{monthsPassed === 1 ? '' : 's'} ago</div>
                      </>
                    )}
                  </td>
                  <td className="border border-gray-200 px-4 py-2.5 text-xs text-gray-600">{s.studentId || '—'}</td>
                  <td className="border border-gray-200 px-4 py-2.5 text-xs text-gray-600">{s.registrationNumber || '—'}</td>
                  <td className="border border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <StudentAvatar imagePath={s.imagePath} name={s.studentName} />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-800">{s.studentName || '—'}</div>
                        <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${deliveryStatusBadgeClass(s.deliveryStatus)}`}>
                          {deliveryStatusLabel(s.deliveryStatus)}
                        </span>
                        <div className="mt-0.5 flex items-center gap-1">
                          <span className={`inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold ${attendanceBadgeClass(s.attendancePercentage)}`}>
                            {s.attendancePercentage === null || s.attendancePercentage === undefined ? '—' : `${s.attendancePercentage}%`} att.
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center gap-1 text-[9px] text-gray-400">
                          <span>Last present: {formatDate(s.lastPresentDate)}</span>
                          {s.lastPresentDate && (
                            <span className="inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold text-gray-500">
                              {daysSince(s.lastPresentDate)}d ago
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {batch.phases.map((phase) => {
                    const data = s.marksByPhase ? s.marksByPhase[phase.phaseId] : null;
                    return (
                      <td key={phase.phaseId} className="border border-gray-200 px-2 py-2.5 text-center">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${data?.marks != null ? attemptBadgeClass(data.attempt) : 'text-gray-300'}`}>
                          {formatMarks(data?.marks)}
                        </span>
                        {attemptLabel(data?.attempt) && (
                          <div className={`mt-0.5 text-[9px] font-semibold ${attemptLabelClass(data.attempt)}`}>
                            {attemptLabel(data.attempt)}
                          </div>
                        )}
                        {data?.examAttemptDatetime && (
                          <div className="mt-0.5 text-emerald-500" title="Exam completed">✓</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DiplomaCompletionReport() {
  const navigate = useNavigate();

  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const [courseOptions, setCourseOptions] = useState([]);
  const [courseOptionsLoading, setCourseOptionsLoading] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]); // [] = all accessible courses
  const [selectedStatus, setSelectedStatus] = useState('ongoing');
  const [search, setSearch] = useState('');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Management" staff get the same unrestricted report access as Super Admin
  // (see RequireReportAccess in App.js).
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  const accessibleParam = useCallback(() => {
    if (isSuperAdmin || isManagement) return 'all';
    return (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
      .map((id) => Number(id))
      .filter((num) => Number.isFinite(num) && num > 0);
  }, [isSuperAdmin, isManagement, accessibleCourseIds]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!accessLoaded) return;
    let isMounted = true;
    (async () => {
      setCourseOptionsLoading(true);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/diploma-completion-report', {
          params: { accessibleCourses: accessibleParam(), courseIds: 'all', batchStatus: 'all' },
        });
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.courses) ? resp.data.courses : [];
        const options = list
          .filter((c) => c.courseId)
          .map((c) => {
            const baseName = c.courseShortName || c.courseName || `Course #${c.courseId}`;
            const scheduleLabel = formatScheduleType(c.scheduleType);
            return { value: c.courseId, label: scheduleLabel ? `${baseName} (${scheduleLabel})` : baseName };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        setCourseOptions(options);
      } catch (e) {
        console.error('Failed to load course options', e);
      } finally {
        if (isMounted) setCourseOptionsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [accessLoaded, accessibleParam]);

  const loadReport = useCallback(async () => {
    if (!accessLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/diploma-completion-report', {
        params: {
          accessibleCourses: accessibleParam(),
          courseIds: selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all',
          batchStatus: selectedStatus,
        },
      });
      const data = resp?.data || {};
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load diploma completion report');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleParam, selectedCourses, selectedStatus]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleStatusChange = useCallback((batchId, registrationId, newStatus) => {
    setCourses((prev) => prev.map((course) => ({
      ...course,
      batches: course.batches.map((batch) => {
        if (batch.batchId !== batchId) return batch;
        return {
          ...batch,
          students: batch.students.map((s) => (
            s.registrationId === registrationId ? { ...s, deliveryStatus: newStatus } : s
          )),
        };
      }),
    })));
  }, []);

  const filteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return courses;
    return courses
      .map((course) => ({
        ...course,
        batches: course.batches
          .map((batch) => ({
            ...batch,
            students: batch.students.filter((s) => {
              const combined = [s.studentName, s.studentId, s.registrationNumber, s.universityID].filter(Boolean).join(' ').toLowerCase();
              return combined.includes(term)
                || (batch.batchInternalName || '').toLowerCase().includes(term)
                || (batch.batchName || '').toLowerCase().includes(term)
                || (course.courseName || '').toLowerCase().includes(term);
            }),
          }))
          .filter((batch) => batch.students.length > 0
            || (batch.batchInternalName || '').toLowerCase().includes(term)
            || (batch.batchName || '').toLowerCase().includes(term)),
      }))
      .filter((course) => course.batches.length > 0);
  }, [courses, search]);

  const visibleCounts = useMemo(() => {
    let batches = 0;
    let students = 0;
    filteredCourses.forEach((course) => {
      batches += course.batches.length;
      course.batches.forEach((batch) => {
        students += batch.students.length;
      });
    });
    return { batches, students };
  }, [filteredCourses]);

  const handlePrint = () => {
    const params = new URLSearchParams();
    params.set('courseIds', selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all');
    params.set('batchStatus', selectedStatus);
    window.open(`/admin/report/diploma-completion/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout title="Diploma Completion Report">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
            </svg>
            Print PDF
          </button>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Diploma Completion Report
          </h2>
          <p className="mt-1 text-sm text-emerald-900">
            Course-wise, batch-wise diploma phase completion sheet — each student's phase marks and attempt, sourced
            from the diploma results data used on the Diploma Batch Phases page. Read-only here; edit marks there.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {BATCH_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setSelectedStatus(tab.value)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  selectedStatus === tab.value
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700">Courses</label>
              <Select
                isMulti
                isLoading={courseOptionsLoading}
                options={courseOptions}
                value={selectedCourses}
                onChange={(opts) => setSelectedCourses(opts || [])}
                placeholder="All accessible diploma courses (default)…"
                styles={selectStyles}
                closeMenuOnSelect={false}
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Student / University ID / Batch / Course"
                className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            {visibleCounts.batches} batch{visibleCounts.batches === 1 ? '' : 'es'} &middot; {visibleCounts.students} student{visibleCounts.students === 1 ? '' : 's'}
          </h3>
        </div>

        {loading ? (
          <LoadingSpinner label="Loading diploma completion report…" />
        ) : filteredCourses.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            No batches match the current filters.
          </div>
        ) : (
          <div className="space-y-10">
            {filteredCourses.map((course) => (
              <div key={course.courseId ?? course.courseName}>
                <h3 className="mb-3 text-lg font-bold text-gray-900">
                  {course.courseShortName || course.courseName || 'Unknown Course'}
                  {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                </h3>

                <div className="space-y-5">
                  {course.batches.map((batch) => (
                    <BatchPhaseSheetTable key={batch.batchId} batch={batch} onStatusChange={handleStatusChange} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
